import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { track, gaEvent } from "@/lib/analytics";
import { SiteLayout } from "@/components/site/SiteLayout";
import { Button } from "@/components/ui/button";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [courseId, setCourseId] = useState(null);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setCourseId(data.course_id);
        if (data.payment_status === "paid") {
          setStatus("paid");
          track("purchase_success", { session_id: sessionId, course_id: data.course_id });
          // GA4 purchase — fires only on confirmed paid, de-duplicated per transaction_id.
          const txId = data.transaction_id || sessionId;
          const key = `ga_purchase_${txId}`;
          if (txId && !localStorage.getItem(key)) {
            localStorage.setItem(key, "1");
            gaEvent("purchase", {
              transaction_id: txId,
              value: Number(data.amount) || 0,
              currency: "CAD",
              items: [{
                item_id: data.course_id,
                item_name: data.course_title || "Course",
                price: Number(data.amount) || 0,
                quantity: 1,
              }],
            });
          }
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) { setStatus("failed"); return; }
      } catch (e) {}
      if (tries++ < 8) setTimeout(poll, 2000); else setStatus("pending");
    };
    poll();
  }, [sessionId]);

  return (
    <SiteLayout>
      <div className="mx-auto flex max-w-lg flex-col items-center px-6 py-28 text-center">
        {status === "checking" && (<><Loader2 className="h-14 w-14 animate-spin text-[#DCA846]" /><p className="mt-6 text-zinc-300">Confirming your payment securely...</p></>)}
        {status === "paid" && (<>
          <CheckCircle2 className="h-16 w-16 text-emerald-400" />
          <h1 className="mt-6 font-display text-3xl font-bold uppercase">Payment successful!</h1>
          <p className="mt-3 text-zinc-400">Your course is now unlocked and available in My Learning.</p>
          <div className="mt-8 flex gap-3">
            {courseId && <Link to={`/learn/${courseId}`}><Button className="bg-[#DCA846] text-black hover:bg-[#C5963B] font-display uppercase">Start Learning</Button></Link>}
            <Link to="/my-learning"><Button variant="outline" className="border-white/15 text-white">My Learning</Button></Link>
          </div>
        </>)}
        {status === "pending" && (<><Loader2 className="h-14 w-14 animate-spin text-[#DCA846]" /><p className="mt-6 text-zinc-300">Payment is processing. It may take a moment — check My Learning shortly.</p><Link to="/my-learning" className="mt-4"><Button variant="outline" className="border-white/15 text-white">Go to My Learning</Button></Link></>)}
        {(status === "failed" || status === "error") && (<>
          <XCircle className="h-16 w-16 text-red-400" />
          <h1 className="mt-6 font-display text-3xl font-bold uppercase">Payment not completed</h1>
          <p className="mt-3 text-zinc-400">Something went wrong. You were not charged. Please try again.</p>
          <Link to="/store" className="mt-8"><Button className="bg-[#DCA846] text-black">Back to Store</Button></Link>
        </>)}
      </div>
    </SiteLayout>
  );
}

export function PaymentCancel() {
  return (
    <SiteLayout>
      <div className="mx-auto flex max-w-lg flex-col items-center px-6 py-28 text-center">
        <XCircle className="h-16 w-16 text-zinc-500" />
        <h1 className="mt-6 font-display text-3xl font-bold uppercase">Checkout cancelled</h1>
        <p className="mt-3 text-zinc-400">No worries — your card was not charged. You can complete your purchase any time.</p>
        <Link to="/store" className="mt-8"><Button className="bg-[#DCA846] text-black">Back to Store</Button></Link>
      </div>
    </SiteLayout>
  );
}
